import { useState, useEffect } from 'react';
import { format, parseISO, isSameDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Clock, Loader2 } from 'lucide-react';
import type { TimeSlot } from '../../types/appointments';

interface SlotPickerProps {
  slots: TimeSlot[];
  loading: boolean;
  selectedSlot: TimeSlot | null;
  onSelectSlot: (slot: TimeSlot) => void;
  selectedDate: Date;
}

export function SlotPicker({
  slots,
  loading,
  selectedSlot,
  onSelectSlot,
  selectedDate,
}: SlotPickerProps) {
  const [groupedSlots, setGroupedSlots] = useState<Record<string, TimeSlot[]>>({});

  useEffect(() => {
    const grouped: Record<string, TimeSlot[]> = {};
    slots.forEach((slot) => {
      const date = format(parseISO(slot.start), 'yyyy-MM-dd');
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(slot);
    });
    setGroupedSlots(grouped);
  }, [slots]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#0066FF]" />
        <span className="ml-2 text-neutral-600">Chargement des creneaux...</span>
      </div>
    );
  }

  const filteredDates = Object.keys(groupedSlots).filter((dateStr) =>
    isSameDay(parseISO(dateStr), selectedDate) || !selectedDate
  );

  if (filteredDates.length === 0) {
    return (
      <div className="text-center py-8 text-neutral-500">
        <Clock className="h-12 w-12 mx-auto mb-3 text-neutral-300" />
        <p>Aucun creneau disponible pour cette date</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {filteredDates.map((dateStr) => (
        <div key={dateStr}>
          <h4 className="text-sm font-medium text-neutral-700 mb-2">
            {format(parseISO(dateStr), 'EEEE d MMMM', { locale: fr })}
          </h4>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {groupedSlots[dateStr].map((slot) => {
              const isSelected =
                selectedSlot?.start === slot.start && selectedSlot?.end === slot.end;
              return (
                <button
                  key={slot.start}
                  onClick={() => onSelectSlot(slot)}
                  className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                    isSelected
                      ? 'bg-[#0066FF] text-white border-[#0066FF]'
                      : slot.is_off_hours
                        ? 'bg-neutral-100 text-neutral-400 border-neutral-200 hover:bg-neutral-200 hover:text-neutral-600'
                        : 'bg-white text-neutral-700 border-neutral-200 hover:border-[#0066FF]/30 hover:bg-blue-50'
                  }`}
                  title={slot.is_off_hours ? 'Hors horaires configurés' : undefined}
                >
                  {format(parseISO(slot.start), 'HH:mm')}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
